'use client';

import { useState } from 'react';
import {
  ExternalLink, ChevronDown, ChevronLeft, ChevronRight,
  Plus, Swords, DoorOpen, Ban, Tag,
  ArrowDownToLine, ArrowUpFromLine, Coins, Receipt,
  Shield, ArrowUpRight, ArrowDownLeft,
} from 'lucide-react';
import { cn, truncateAddress, formatTimeAgo, formatUSD } from '@/lib/utils';
import { generateGridAvatarUrl } from '@/lib/avatar';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Card, CardContent } from '@/components/ui/card';
import type { V2GridSeatEvent } from '@/lib/types/v2';
import { V2_CONSTANTS } from '@/lib/types/v2';

const EVENT_META: Record<string, { label: string; icon: typeof Plus; color: string }> = {
  BOUGHT_VACANT:       { label: 'Claimed',      icon: Plus,           color: 'text-emerald-400' },
  BOUGHT_FROM_CREATOR: { label: 'Buyout',        icon: Swords,         color: 'text-amber-400' },
  BOUGHT_OUT:          { label: 'Buyout',        icon: Swords,         color: 'text-amber-400' },
  ABANDONED:           { label: 'Abandoned',     icon: DoorOpen,       color: 'text-red-400' },
  FORFEITED:           { label: 'Forfeited',     icon: Ban,            color: 'text-red-400' },
  PRICE_UPDATED:       { label: 'Price Set',     icon: Tag,            color: 'text-purple-400' },
  DEPOSIT_ADDED:       { label: 'Deposit',       icon: ArrowDownToLine, color: 'text-emerald-400' },
  DEPOSIT_WITHDRAWN:   { label: 'Withdraw',      icon: ArrowUpFromLine, color: 'text-orange-400' },
  YIELD_CLAIMED:       { label: 'Yield',         icon: Coins,          color: 'text-emerald-400' },
  TAX_COLLECTED:       { label: 'Tax',           icon: Receipt,        color: 'text-muted-foreground' },
  ANTI_SNIPE_TRIGGERED:{ label: 'Anti-snipe',    icon: Shield,         color: 'text-blue-400' },
  MIGRATED_OUT:        { label: 'Migrated Out',  icon: ArrowUpRight,   color: 'text-muted-foreground' },
  MIGRATED_IN:         { label: 'Migrated In',   icon: ArrowDownLeft,  color: 'text-muted-foreground' },
};

const PAGE_SIZE = 10;
const MAX_PREVIEW = 3;

interface ActivityCardProps {
  events: V2GridSeatEvent[];
}

export function ActivityCard({ events }: ActivityCardProps) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);

  const totalPages = Math.max(1, Math.ceil(events.length / PAGE_SIZE));
  const pagedEvents = events.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const previewEvents = events.slice(0, MAX_PREVIEW);

  const explorerBase = process.env.NEXT_PUBLIC_CHAIN_ID === '84532'
    ? 'https://sepolia.basescan.org'
    : 'https://basescan.org';

  return (
    <Card>
      <button
        onClick={() => { setOpen(v => !v); setPage(0); }}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-muted/30 transition-colors rounded-t-lg"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">Activity</span>
          <span className="text-[10px] font-mono text-muted-foreground">
            {events.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!open && previewEvents.length > 0 && (
            <div className="flex items-center -space-x-1.5">
              {previewEvents.map((event, i) => (
                <Tooltip key={event.id} delayDuration={100}>
                  <TooltipTrigger asChild>
                    <div
                      className="w-6 h-6 rounded-md overflow-hidden border-2 border-card bg-muted shrink-0"
                      style={{ zIndex: i + 1 }}
                    >
                      <img
                        src={event.userIdentity?.avatarUrl || generateGridAvatarUrl(event.user, 24)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs px-2 py-1">
                    {event.userIdentity?.displayName || truncateAddress(event.user, 3)} · {formatTimeAgo(event.timestamp * 1000)}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          )}
          <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </div>
      </button>
      {open && (
        <CardContent className="p-3 pt-0 space-y-1">
          {events.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No activity yet</p>
          ) : (
            <>
              <div className="space-y-0.5">
                {pagedEvents.map((event) => {
                  const meta = EVENT_META[event.eventType] || { label: event.eventType, icon: Plus, color: 'text-muted-foreground' };
                  const Icon = meta.icon;
                  const priceOrAmount = event.price || event.amount;
                  const valueNum = priceOrAmount ? Number(priceOrAmount) / 10 ** V2_CONSTANTS.USDC_DECIMALS : 0;
                  const seatNum = event.seatId ? event.seatId.split('-').pop() : undefined;

                  return (
                    <div
                      key={event.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/30 transition-colors text-xs"
                    >
                      <Tooltip delayDuration={100}>
                        <TooltipTrigger asChild>
                          <span className={cn('shrink-0', meta.color)}>
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs px-2 py-1">{meta.label}</TooltipContent>
                      </Tooltip>
                      {seatNum !== undefined && (
                        <span className="text-muted-foreground w-6 shrink-0">#{seatNum}</span>
                      )}
                      <div className="flex items-center gap-1 min-w-0 flex-1">
                        <img
                          src={event.userIdentity?.avatarUrl || generateGridAvatarUrl(event.user, 12)}
                          alt=""
                          className="w-3.5 h-3.5 rounded-sm shrink-0"
                        />
                        <span className="text-muted-foreground truncate">
                          {event.userIdentity?.displayName || truncateAddress(event.user, 3)}
                        </span>
                      </div>
                      {valueNum > 0 && (
                        <span className="text-muted-foreground shrink-0">{formatUSD(valueNum)}</span>
                      )}
                      <span className="text-[10px] text-muted-foreground/60 shrink-0 w-10 text-right">
                        {formatTimeAgo(event.timestamp * 1000)}
                      </span>
                      <a
                        href={`${explorerBase}/tx/${event.txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground/40 hover:text-foreground shrink-0"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-1.5 border-t border-border/30">
                  <button
                    onClick={(e) => { e.stopPropagation(); setPage(p => Math.max(0, p - 1)); }}
                    disabled={page === 0}
                    className={cn(
                      'p-1 rounded transition-colors',
                      page === 0 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                    )}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <span className="text-[10px] text-muted-foreground font-mono">
                    {page + 1} / {totalPages}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setPage(p => Math.min(totalPages - 1, p + 1)); }}
                    disabled={page >= totalPages - 1}
                    className={cn(
                      'p-1 rounded transition-colors',
                      page >= totalPages - 1 ? 'text-muted-foreground/30 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                    )}
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}
